// Run analytics: splits + bounds + formatters.

export function haversineMeters(a, b) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

const METERS_PER_MILE = 1609.344;

/** Build per-segment splits.
 *  unit = "km" → splits every 1000m | unit = "mi" → splits every 1609.344m
 *  Each split: { idx, label, duration_seconds, pace_min_per_unit, distance_m }
 */
export function computeSplits(gpsPath, unit = "km") {
    if (!gpsPath || gpsPath.length < 2) return [];
    const unitMeters = unit === "mi" ? METERS_PER_MILE : 1000;
    const splits = [];
    let cum = 0;             // cumulative distance in meters
    let lastSplitAt = 0;     // meters where the last split started
    let lastSplitTime = gpsPath[0].t;
    for (let i = 1; i < gpsPath.length; i++) {
        const prev = gpsPath[i - 1];
        const cur = gpsPath[i];
        const seg = haversineMeters(prev, cur);
        cum += seg;
        // While cumulative distance has crossed one or more split boundaries, emit them.
        while (cum - lastSplitAt >= unitMeters) {
            // Linearly interpolate the time for the exact split boundary inside this segment.
            const overshoot = cum - (lastSplitAt + unitMeters);
            const ratio = seg > 0 ? 1 - overshoot / seg : 1;
            const splitTime = prev.t + (cur.t - prev.t) * ratio;
            const dur = (splitTime - lastSplitTime) / 1000;
            splits.push({
                idx: splits.length + 1,
                label: `${splits.length + 1}`,
                duration_seconds: dur,
                pace_min_per_unit: dur / 60,
                distance_m: unitMeters,
            });
            lastSplitAt += unitMeters;
            lastSplitTime = splitTime;
        }
    }
    // Partial last split — only show when there's meaningful trailing distance
    const trailing = cum - lastSplitAt;
    if (trailing > 50) {
        const lastT = gpsPath[gpsPath.length - 1].t;
        const dur = (lastT - lastSplitTime) / 1000;
        splits.push({
            idx: splits.length + 1,
            label: `${splits.length + 1}*`,
            duration_seconds: dur,
            pace_min_per_unit: trailing > 0 ? dur / 60 / (trailing / unitMeters) : 0,
            distance_m: trailing,
            partial: true,
        });
    }
    return splits;
}

/** Compute geo bounding box from gps points; returns [[w,s],[e,n]] or null. */
export function computeBounds(gpsPath) {
    if (!gpsPath || gpsPath.length === 0) return null;
    let minLng = gpsPath[0].lng, maxLng = gpsPath[0].lng;
    let minLat = gpsPath[0].lat, maxLat = gpsPath[0].lat;
    for (const p of gpsPath) {
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
    }
    return [
        [minLng, minLat],
        [maxLng, maxLat],
    ];
}

/** Extract elevation series from gps_path. Returns array of { d_m, alt } points sampled from cumulative. */
export function elevationSeries(gpsPath) {
    if (!gpsPath || gpsPath.length === 0) return [];
    let cum = 0;
    const series = [];
    for (let i = 0; i < gpsPath.length; i++) {
        if (i > 0) cum += haversineMeters(gpsPath[i - 1], gpsPath[i]);
        const alt = gpsPath[i].alt;
        if (alt != null && !Number.isNaN(alt)) {
            series.push({ d_m: cum, alt });
        }
    }
    return series;
}

export function fmtPace(decimalMin) {
    if (!isFinite(decimalMin) || decimalMin <= 0) return "—";
    const m = Math.floor(decimalMin);
    const s = Math.round((decimalMin - m) * 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

export function fmtTime(s) {
    s = Math.max(0, Math.floor(s));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
        : `${m}:${String(sec).padStart(2, "0")}`;
}

export function fmtDistance(m, unit = "km") {
    if (unit === "mi") return (m / METERS_PER_MILE).toFixed(2);
    return (m / 1000).toFixed(2);
}
